import { LoggerService } from 'src/app/utils/logger.service';

// src/app/utils/browser-camera.service.ts
import { Injectable } from '@angular/core';

export type FacingMode = 'user' | 'environment';

export interface BrowserCameraOptions {
  facingMode?: FacingMode;  // 'environment' par défaut
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;         // 0..1 (0.92 par défaut)
  fileName?: string;
}

export interface BrowserCameraResult {
  file: File | null;
  cancelled: boolean;
  error?: string;           // défini seulement en cas d'erreur réelle
}

@Injectable({ providedIn: 'root' })
export class CameraViewerService {

  public async takePicture(options: BrowserCameraOptions = {}): Promise<BrowserCameraResult> {
    const supportsMedia = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    if (!supportsMedia) return await this.captureViaInput(options);
    return await this.captureViaOverlay(options);
  }

  // ============================ Overlay caméra ============================

  private async captureViaOverlay(opts: BrowserCameraOptions): Promise<BrowserCameraResult> {
    const {
      facingMode = 'environment',
      maxWidth,
      maxHeight,
      quality = 0.92,
      fileName
    } = opts || {};

    // styles utilitaires (une seule injection)
    const styleId = 'browser-camera-styles';
    if (!document.getElementById(styleId)) {
      const st = document.createElement('style');
      st.id = styleId;
      st.textContent = `
      @keyframes bc_spin { to { transform: rotate(360deg); } }
      .bc-shadow { box-shadow: 0 10px 30px rgba(0,0,0,.35); }
      .bc-btn:hover { filter: brightness(1.08); }
      .bc-btn:active { transform: translateY(1px); }
      .bc-hidden { display: none !important; }
      .bc-ctrl { width:40px;height:40px;border-radius:12px;border:1px solid rgba(255,255,255,.14);background:rgba(255,255,255,.06);display:grid;place-items:center;color:#fff;cursor:pointer }
      .bc-ctrl[disabled]{opacity:.4;pointer-events:none}

      /* --- Shutter style Android 15 : anneau blanc, centre transparent --- */
      .bc-shutter{
        width:76px;height:76px;border-radius:999px;background:transparent;
        border:4px solid rgba(255,255,255,0.95);display:grid;place-items:center;cursor:pointer;
        position:relative;transition: transform .06s ease, border-color .12s ease;
      }
      .bc-shutter::after{
        content:"";position:absolute;inset:10px;border-radius:999px;
        border:2px solid rgba(255,255,255,0.35);
      }
      .bc-shutter:active{ transform: scale(0.97); }
      .bc-shutter:focus-visible{ outline: none; box-shadow: 0 0 0 3px rgba(255,255,255,.25); }

      .bc-approve { width:68px;height:68px;border-radius:999px;border:0;background:linear-gradient(180deg,#2dd36f,#12b35a);display:grid;place-items:center;cursor:pointer;box-shadow:0 6px 16px rgba(18,179,90,.25) }
      .bc-retake  { width:48px;height:48px;border-radius:12px;border:1px solid rgba(255,255,255,.14);background:rgba(255,255,255,.06);display:grid;place-items:center;color:#fff;cursor:pointer }
      `;
      document.head.appendChild(st);
    }

    const cleanup: Array<() => void> = [];
    let stream: MediaStream | null = null;
    let videoInputs: MediaDeviceInfo[] = [];
    let currentIndex = 0;

    // ---------- Overlay plein écran ----------
    const overlay = document.createElement('div');
    Object.assign(overlay.style, {
      position: 'fixed',
      inset: '0',
      backdropFilter: 'blur(4px)',
      background: 'rgba(0,0,0,0.55)',
      zIndex: '2147483647',
      display: 'grid',
      placeItems: 'center',
      padding: '16px'
    });
    document.body.appendChild(overlay);
    cleanup.push(() => overlay.remove());

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    cleanup.push(() => { document.body.style.overflow = prevOverflow; });

    // ---------- Carte ----------
    const card = document.createElement('div');
    Object.assign(card.style, {
      width: 'min(92vw, 760px)',
      maxWidth: '760px',
      height: 'min(92vh, 890px)',
      background: 'rgba(28,28,30,0.88)',
      color: '#fff',
      borderRadius: '20px',
      border: '1px solid rgba(255,255,255,0.08)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      position: 'relative'
    });
    card.classList.add('bc-shadow');
    overlay.appendChild(card);

    // Header (titre + close)
    const header = document.createElement('div');
    Object.assign(header.style, {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.08)'
    });
    const title = document.createElement('div');
    title.textContent = 'Camera';
    Object.assign(title.style, { fontWeight: '600', opacity: '.9', letterSpacing: '.2px' });

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'bc-btn bc-ctrl';
    closeBtn.innerHTML = this.svgX();
    header.append(title, closeBtn);
    card.appendChild(header);

    // Zone média
    const mediaWrap = document.createElement('div');
    Object.assign(mediaWrap.style, {
      position: 'relative',
      background: '#000',
      flex: '1 1 auto',
      minHeight: '240px'
    });

    const video = document.createElement('video');
    video.setAttribute('playsinline', '');
    video.setAttribute('muted', '');
    video.autoplay = true;
    video.playsInline = true;
    video.muted = true;
    Object.assign(video.style, {
      position: 'absolute', inset: '0', width: '100%', height: '100%', objectFit: 'cover'
    });

    const previewImg = document.createElement('img');
    previewImg.decoding = 'async';
    Object.assign(previewImg.style, {
      position: 'absolute', inset: '0', width: '100%', height: '100%', objectFit: 'contain',
      background: '#000'
    });
    previewImg.classList.add('bc-hidden');

    const spinner = document.createElement('div');
    Object.assign(spinner.style, {
      position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
      width: '42px', height: '42px', border: '3px solid rgba(255,255,255,.25)',
      borderTopColor: '#fff', borderRadius: '50%', animation: 'bc_spin .9s linear infinite'
    });

    // Barre de contrôle superposée
    const controlsBar = document.createElement('div');
    Object.assign(controlsBar.style, {
      position: 'absolute',
      left: '0', right: '0', bottom: '0',
      padding: '12px 14px',
      display: 'grid',
      gridTemplateColumns: '1fr auto 1fr',
      alignItems: 'center',
      background: 'linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,.55) 35%, rgba(0,0,0,.65))'
    });

    // --- Boutons (SVG only)
    const retakeBtn = document.createElement('button'); // preview: gauche
    retakeBtn.type = 'button';
    retakeBtn.className = 'bc-btn bc-retake bc-hidden';
    retakeBtn.title = 'Retake';
    retakeBtn.innerHTML = this.svgRetake();

    const shutterBtn = document.createElement('button'); // capture: centre
    shutterBtn.type = 'button';
    shutterBtn.className = 'bc-btn bc-shutter';
    shutterBtn.title = 'Capture';

    const approveBtn = document.createElement('button'); // preview: centre (check vert)
    approveBtn.type = 'button';
    approveBtn.className = 'bc-btn bc-approve bc-hidden';
    approveBtn.title = 'Use photo';
    approveBtn.innerHTML = this.svgCheck();

    const flipBtn = document.createElement('button'); // droite (capture only)
    flipBtn.type = 'button';
    flipBtn.className = 'bc-btn bc-ctrl';
    flipBtn.title = 'Flip camera';
    flipBtn.innerHTML = this.svgFlip();

    // Layout: [retake] [center big] [flip/empty]
    const leftSlot = document.createElement('div'); leftSlot.style.display = 'flex'; leftSlot.style.justifyContent = 'flex-start';
    const midSlot = document.createElement('div'); midSlot.style.display = 'grid'; midSlot.style.placeItems = 'center';
    const rightSlot = document.createElement('div'); rightSlot.style.display = 'flex'; rightSlot.style.justifyContent = 'flex-end';

    leftSlot.append(retakeBtn);
    midSlot.append(shutterBtn, approveBtn);
    rightSlot.append(flipBtn);
    controlsBar.append(leftSlot, midSlot, rightSlot);

    mediaWrap.append(video, previewImg, spinner, controlsBar);
    card.appendChild(mediaWrap);

    const canvas = document.createElement('canvas');

    // UI states
    const showPreviewUI = (show: boolean) => {
      previewImg.classList.toggle('bc-hidden', !show);
      video.classList.toggle('bc-hidden', show);
      shutterBtn.classList.toggle('bc-hidden', show);
      approveBtn.classList.toggle('bc-hidden', !show);
      retakeBtn.classList.toggle('bc-hidden', !show);
      flipBtn.classList.toggle('bc-hidden', show);
      title.textContent = show ? 'Preview' : 'Camera';
    };

    // events: close
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') cancel(); };
    window.addEventListener('keydown', onKey);
    cleanup.push(() => window.removeEventListener('keydown', onKey));
    const cancel = () => {
      clearPreviewUrl();
      cleanup.forEach(fn => fn());
      resolver({ file: null, cancelled: true });
    };
    overlay.addEventListener('click', (e) => { if (e.target === overlay) cancel(); });
    closeBtn.onclick = cancel;

    const stopStream = () => {
      if (stream) { stream.getTracks().forEach(t => t.stop()); stream = null; }
    };
    cleanup.push(stopStream);

    const waitForVideoReady = async (): Promise<void> => {
      if (video.readyState >= 2 && video.videoWidth > 0) return;
      await new Promise<void>(res => {
        const onReady = () => {
          if (video.videoWidth > 0) {
            video.removeEventListener('loadedmetadata', onReady);
            video.removeEventListener('canplay', onReady);
            res();
          }
        };
        video.addEventListener('loadedmetadata', onReady, { once: true });
        video.addEventListener('canplay', onReady, { once: true });
      });
      await new Promise(requestAnimationFrame);
      if (video.videoWidth === 0) await new Promise(r => setTimeout(r, 50));
    };

    const setMirrorByTrack = () => {
      const track = stream?.getVideoTracks()[0];
      const settings: any = track?.getSettings?.();
      const label = track?.label || '';
      const isFront = settings?.facingMode === 'user' || /front|user/i.test(label);
      video.style.transform = isFront ? 'scaleX(-1)' : 'none';
    };

    const openStreamByDeviceId = async (deviceId?: string, retryIfZeroWidth = true) => {
      stopStream();
      const constraints: MediaStreamConstraints = deviceId
        ? { video: { deviceId: { exact: deviceId } }, audio: false }
        : { video: { facingMode: { ideal: facingMode } }, audio: false };

      spinner.style.display = 'block';
      stream = await navigator.mediaDevices.getUserMedia(constraints);
      video.srcObject = stream;

      await waitForVideoReady().catch(() => { });
      try { await video.play(); } catch { }
      if (retryIfZeroWidth && (!video.videoWidth || !video.videoHeight)) {
        try {
          const all = await navigator.mediaDevices.enumerateDevices();
          const cams = all.filter(d => d.kind === 'videoinput');
          if (cams.length) {
            const envIdx = cams.findIndex(d => /back|rear|environment/i.test(d.label));
            const idx = envIdx >= 0 ? envIdx : 0;
            await openStreamByDeviceId(cams[idx].deviceId, /*retryIfZeroWidth*/ false);
          }
        } catch { }
      }
      spinner.style.display = 'none';
      setMirrorByTrack();
    };

    const pickInitialIndex = (devices: MediaDeviceInfo[]) => {
      const envIdx = devices.findIndex(d => /back|rear|environment/i.test(d.label));
      const userIdx = devices.findIndex(d => /front|user/i.test(d.label));
      if (facingMode === 'environment' && envIdx >= 0) return envIdx;
      if (facingMode === 'user' && userIdx >= 0) return userIdx;
      return facingMode === 'environment' ? Math.max(0, devices.length - 1) : 0;
    };

    // ouverture initiale
    try { await openStreamByDeviceId(undefined, /*retryIfZeroWidth*/ true); }
    catch { cleanup.forEach(fn => fn()); return await this.captureViaInput(opts); }

    // enumerate + flip
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      videoInputs = all.filter(d => d.kind === 'videoinput');
      if (videoInputs.length <= 1) {
        flipBtn.setAttribute('disabled', 'true');
      } else {
        currentIndex = pickInitialIndex(videoInputs);
        const currentSettings: any = stream?.getVideoTracks()[0]?.getSettings?.();
        if (videoInputs[currentIndex]?.deviceId && currentSettings?.deviceId !== videoInputs[currentIndex].deviceId) {
          await openStreamByDeviceId(videoInputs[currentIndex].deviceId);
        }
      }
    } catch { flipBtn.setAttribute('disabled', 'true'); }

    flipBtn.onclick = async () => {
      if (videoInputs.length <= 1) return;
      try {
        currentIndex = (currentIndex + 1) % videoInputs.length;
        await openStreamByDeviceId(videoInputs[currentIndex].deviceId);
      } catch {
        try {
          currentIndex = (currentIndex + 1) % videoInputs.length;
          await openStreamByDeviceId(videoInputs[currentIndex].deviceId);
        } catch { }
      }
    };

    // ---------- Cycle Capture → Preview ----------
    let lastBlob: Blob | null = null;
    let lastObjectUrl: string | null = null;
    const clearPreviewUrl = () => {
      if (lastObjectUrl) { URL.revokeObjectURL(lastObjectUrl); lastObjectUrl = null; }
    };

    // Capture → entre en mode preview
    shutterBtn.onclick = async () => {
      try {
        const { videoWidth, videoHeight } = video;
        if (!videoWidth || !videoHeight) throw new Error('Video not ready');

        const ratio = videoWidth / videoHeight;
        let outW = videoWidth, outH = videoHeight;
        if (maxWidth && outW > maxWidth) { outW = maxWidth; outH = Math.round(outW / ratio); }
        if (maxHeight && outH > maxHeight) { outH = maxHeight; outW = Math.round(outH * ratio); }

        canvas.width = outW; canvas.height = outH;
        const ctx = canvas.getContext('2d')!;

        // image non-miroir si front
        const track = stream?.getVideoTracks()[0];
        const settings: any = track?.getSettings?.();
        const label = track?.label || '';
        const isFront = settings?.facingMode === 'user' || /front|user/i.test(label);
        if (isFront) { ctx.translate(outW, 0); ctx.scale(-1, 1); }

        ctx.drawImage(video, 0, 0, outW, outH);

        lastBlob = await new Promise<Blob>((res, rej) =>
          canvas.toBlob((b) => b ? res(b) : rej(new Error('toBlob failed')), 'image/jpeg', quality)
        );

        clearPreviewUrl();
        lastObjectUrl = URL.createObjectURL(lastBlob);
        previewImg.src = lastObjectUrl;

        showPreviewUI(true);
      } catch (e) { LoggerService.error('Erreur lors de la capture d\'image', e); }
    };

    // Retake ← revient au flux
    retakeBtn.onclick = () => {
      showPreviewUI(false);
      clearPreviewUrl();
      lastBlob = null;
    };

    // Use photo (check centré)
    approveBtn.onclick = () => {
      try {
        if (!lastBlob) throw new Error('No photo');
        const name = fileName ?? this.makeFileName();
        const file = new File([lastBlob], name, { type: 'image/jpeg' });
        clearPreviewUrl();
        cleanup.forEach(fn => fn());
        resolver({ file, cancelled: false });
      } catch (e: any) {
        LoggerService.error('Erreur lors de la validation de la photo', e);
        clearPreviewUrl();
        cleanup.forEach(fn => fn());
        resolver({ file: null, cancelled: false, error: e?.message || 'confirm_failed' });
      }
    };

    // promesse résultat
    let resolver!: (r: BrowserCameraResult) => void;
    const result = new Promise<BrowserCameraResult>((resolve) => { resolver = resolve; });
    return result;
  }

  // ======================== Fallback <input type="file"> ========================

  private captureViaInput(opts: BrowserCameraOptions): Promise<BrowserCameraResult> {
    const { quality = 0.92, maxWidth, maxHeight, fileName } = opts || {};
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      (input as any).capture = 'environment';
      Object.assign(input.style, { position: 'fixed', opacity: '0', pointerEvents: 'none' });
      document.body.appendChild(input);

      const cleanup = () => input.remove();

      input.onchange = async () => {
        try {
          const file = input.files && input.files[0];
          if (!file) { cleanup(); return resolve({ file: null, cancelled: true }); }

          if (!maxWidth && !maxHeight) {
            if (fileName) {
              const buf = await file.arrayBuffer();
              const renamed = new File([buf], fileName, { type: file.type });
              cleanup(); return resolve({ file: renamed, cancelled: false });
            }
            cleanup(); return resolve({ file, cancelled: false });
          }

          const img = document.createElement('img');
          img.decoding = 'async'; img.loading = 'eager';
          const url = URL.createObjectURL(file);
          img.src = url; await img.decode().catch(() => { }); URL.revokeObjectURL(url);

          const ratio = img.naturalWidth / img.naturalHeight;
          let outW = img.naturalWidth, outH = img.naturalHeight;
          if (maxWidth && outW > maxWidth) { outW = maxWidth; outH = Math.round(outW / ratio); }
          if (maxHeight && outH > maxHeight) { outH = maxHeight; outW = Math.round(outH * ratio); }

          const canvas = document.createElement('canvas');
          canvas.width = outW; canvas.height = outH;
          const ctx = canvas.getContext('2d')!;
          ctx.drawImage(img, 0, 0, outW, outH);

          const blob: Blob = await new Promise((res, rej) =>
            canvas.toBlob((b) => b ? res(b) : rej(new Error('toBlob failed')), 'image/jpeg', quality)
          );
          const renamed = new File([blob], fileName ?? this.makeFileName(), { type: 'image/jpeg' });
          cleanup(); resolve({ file: renamed, cancelled: false });
        } catch (e: any) {
          LoggerService.error('Erreur lors de la capture via input', e);
          cleanup(); resolve({ file: null, cancelled: false, error: e?.message || 'input_failed' });
        }
      };

      input.click();
      setTimeout(() => {
        window.addEventListener('focus', () => {
          setTimeout(() => {
            if (!input.files || input.files.length === 0) {
              cleanup(); resolve({ file: null, cancelled: true });
            }
          }, 0);
        }, { once: true });
      }, 0);
    });
  }

  private makeFileName(): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    const d = new Date();
    return `photo_${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}.jpg`;
  }

  // ----------------------------- SVG icônes -----------------------------
  private svgX(): string {
    return `
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
           xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
  }
  private svgFlip(): string {
    return `
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
           xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M3 12a9 9 0 0 1 14.31-7.03M21 12a9 9 0 0 1-14.31 7.03"
              stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        <path d="M7 12l-3-2v4l3-2zm10 0l3 2v-4l-3 2z" fill="currentColor"/>
      </svg>
    `;
  }
  private svgRetake(): string {
    return `
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
           xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M4 4v6h6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M20 20v-6h-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M20 9a8 8 0 0 0-13.66-3.66M4 15a8 8 0 0 0 13.66 3.66" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>
    `;
  }
  private svgCheck(): string {
    return `
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
           xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M20 6L9 17l-5-5" stroke="#ffffff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
  }
}
